/**
 * ResultsList — directory org results grid (Block 6.F.2 → X.6.5B reskin;
 * TypeScript per UX-13 S1, markup unchanged).
 *
 * Auto-fill grid of ResourceCards + "Show more" pagination (visibleCount += 30).
 * X.6.5B: token empty state (Lucide icon) + token show-more pill; the grid flows
 * to the wider refresh cards. No raw hexes.
 */

import { type ReactNode } from 'react'
import { SearchX } from 'lucide-react'
import { ResourceCard } from './ResourceCard'
import type { ProviderForSearch } from '@/lib/types'

export interface ResultsListProps {
  results: ProviderForSearch[]
  visibleCount: number
  onShowMore: () => void
  onSelect: (resource: ProviderForSearch) => void
  /** False when the page shows the "Showing N of M" line itself, beside
   *  its Sort control (the Directory). */
  showCount?: boolean
}

export function ResultsList({ results, visibleCount, onShowMore, onSelect, showCount = true }: ResultsListProps): ReactNode {
  const visible = results.slice(0, visibleCount)
  const remaining = results.length - visibleCount

  if (visible.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <SearchX size={40} aria-hidden="true" className="mx-auto mb-3 text-sage-300" />
        <p className="mb-1 text-base font-semibold text-sage-800">Try another word, or widen the filters.</p>
        <p className="text-sm text-ink-soft">Try broadening your search or removing filters</p>
      </div>
    )
  }

  return (
    <div>
      {showCount && (
        <div className="mb-3 flex items-center justify-between" aria-live="polite">
          <span className="text-[13px] font-medium text-ink-soft">
            Showing {Math.min(visibleCount, results.length).toLocaleString()} of{' '}
            {results.length.toLocaleString()}
          </span>
        </div>
      )}

      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(338px, 100%), 1fr))' }}>
        {visible.map((r) => (
          <ResourceCard key={r.id} resource={r} onClick={onSelect} />
        ))}
      </div>

      {remaining > 0 && (
        <button
          onClick={onShowMore}
          className="mx-auto mt-5 block rounded-pill border border-sage-800/[8%] bg-paper px-8 py-3 text-sm font-semibold text-sage-600 shadow-sh1 transition-all duration-[var(--dur-base)] ease-quiet hover:text-sage-800 hover:shadow-sh2 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          Show more ({Math.min(remaining, 30)} more)
        </button>
      )}
    </div>
  )
}
